/**
 * Content Package Select component.
 *
 * A select dropdown for choosing a content package, displaying labels and size.
 * Used for single content package selection (e.g., Dockerfile build context).
 *
 * @module components/ui/ContentPackageSelect
 */

import { Select } from './Select';
import type { ContentPackageResponse } from '@/types';

export interface ContentPackageSelectProps {
  /** Label for the select */
  label?: string;
  /** Currently selected content package ID */
  value: string;
  /** Called when selection changes */
  onChange: (contentPackageId: string) => void;
  /** Available content packages */
  contentPackages: ContentPackageResponse[] | undefined;
  /** Whether content packages are loading */
  isLoading?: boolean;
  /** Whether to include a "None" option */
  allowNone?: boolean;
  /** Label for the none option */
  noneLabel?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Optional helper text */
  helperText?: string;
}

/**
 * Formats a content package for display in a select option.
 * Shows name (if available), other labels, and size.
 */
function formatContentPackageLabel(pkg: ContentPackageResponse): string {
  const name = pkg.labels?.['name'];
  const otherLabels = pkg.labels
    ? Object.entries(pkg.labels)
        .filter(([k]) => k !== 'name')
        .map(([k, v]) => `${k}=${v}`)
        .slice(0, 2)
        .join(', ')
    : '';
  const sizeKb = Math.round(pkg.size / 1024);

  let label = name || `${pkg.id.slice(0, 8)}...`;
  if (otherLabels) {
    label += ` [${otherLabels}]`;
  }
  label += ` (${sizeKb} KB)`;

  return label;
}

/**
 * Select component for choosing a single content package.
 */
export function ContentPackageSelect({
  label = 'Content Package',
  value,
  onChange,
  contentPackages,
  isLoading = false,
  allowNone = false,
  noneLabel = 'None',
  placeholder,
  disabled = false,
  helperText,
}: ContentPackageSelectProps) {
  const options = [
    ...(allowNone ? [{ value: '', label: noneLabel }] : []),
    ...(contentPackages?.map((pkg) => ({
      value: pkg.id,
      label: formatContentPackageLabel(pkg),
    })) || []),
  ];

  return (
    <div>
      <Select
        label={label}
        options={options}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isLoading ? 'Loading content packages...' : placeholder}
        disabled={disabled || isLoading}
      />
      {helperText && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
